import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import {
  Search,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Video,
  Clock,
  AlertTriangle,
  Undo2,
  FileText,
  Pill,
  Award,
  Activity,
  CheckCircle2,
  DollarSign,
  User,
  ShieldCheck,
  ExternalLink,
  Phone,
  Mail,
} from 'lucide-react';
import { adminApi } from '@/api/http-client';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';

type AppointmentStatusFilter =
  | 'ALL'
  | 'WAITING_PAYMENT'
  | 'PAYMENT_PENDING'
  | 'PAYMENT_FAILED'
  | 'PAID'
  | 'WAITING_DOCTOR'
  | 'DOCTOR_ACCEPTED'
  | 'IN_PROGRESS'
  | 'FINISHED'
  | 'CANCELLED'
  | 'NO_SHOW'
  | 'REFUNDED'
  | 'PARTIALLY_REFUNDED'
  | 'RESCHEDULED'
  | 'ARCHIVED'
  | 'EXPIRED';

type ServiceTypeFilter = 'ALL' | 'TELEMEDICINA' | 'PRESENCIAL' | 'DOMICILIAR';

export interface Appointment {
  id: string;
  patientName: string;
  patientEmail: string;
  patientPhone?: string | null;
  patientCpf?: string | null;
  dependentName?: string | null;
  dependentRelationship?: string | null;
  doctorName: string;
  doctorEmail?: string | null;
  doctorCrm?: string | null;
  specialty: string;
  date: string;
  time: string;
  status: string;
  type: string;
  serviceType: string;
  price: number;
  displacementFee?: number;
  reason?: string | null;
  notes?: string | null;
  cancellationReason?: string | null;
  cancelledBy?: string | null;
  cancelledAt?: string | null;
  medicalRecordId?: string | null;
  videoRoomId?: string | null;
  prescriptions?: Array<{
    id: string;
    memedPrescriptionId?: string | null;
    memedPdfUrl?: string | null;
    memedStatus?: string | null;
    createdAt: string;
  }>;
  certificates?: Array<{
    id: string;
    pdfUrl?: string | null;
    diagnosis?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    createdAt: string;
  }>;
  exams?: Array<{
    id: string;
    type?: string | null;
    status?: string | null;
    description?: string | null;
  }>;
}

interface AppointmentsResponse {
  appointments: Appointment[];
  total: number;
  page: number;
  pageSize: number;
}

export function AdminAppointmentsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<AppointmentStatusFilter>('ALL');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [serviceFilter, setServiceFilter] = useState<ServiceTypeFilter>('ALL');
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  // Cancellation modal state
  const [appointmentToCancel, setAppointmentToCancel] = useState<Appointment | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Refund modal state
  const [appointmentToRefund, setAppointmentToRefund] = useState<Appointment | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [isRefunding, setIsRefunding] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [refundSuccess, setRefundSuccess] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery<AppointmentsResponse>({
    queryKey: ['admin', 'appointments', page, filter, typeFilter, serviceFilter, search],
    queryFn: async (): Promise<AppointmentsResponse> => {
      const response = await adminApi.getAppointments({
        page,
        limit: 10,
        status: filter !== 'ALL' ? filter : undefined,
        type: typeFilter !== 'ALL' ? typeFilter : undefined,
        serviceType: serviceFilter !== 'ALL' ? serviceFilter : undefined,
        search: search.trim() || undefined,
      });

      return {
        appointments: response.data.map((apt) => {
          const scheduledDate = new Date(apt.scheduledAt);
          const hasValidDate = !isNaN(scheduledDate.getTime());
          const datePart = hasValidDate ? scheduledDate.toISOString().split('T')[0] : '';
          const date: string = datePart || '';
          const time: string = hasValidDate
            ? scheduledDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
            : '';

          return {
            id: apt.id,
            patientName: apt.patientName || 'Paciente',
            patientEmail: apt.patientEmail || 'N/A',
            patientPhone: apt.patientPhone,
            patientCpf: apt.patientCpf,
            dependentName: apt.dependentName,
            dependentRelationship: apt.dependentRelationship,
            doctorName: apt.doctorName || 'Médico',
            doctorEmail: apt.doctorEmail,
            doctorCrm: apt.doctorCrm,
            specialty: apt.doctorSpecialty || 'Clínica Geral',
            date,
            time,
            status: apt.status,
            type: apt.type,
            serviceType: apt.serviceType || 'TELEMEDICINA',
            price: Number(apt.price) || 0,
            displacementFee: Number(apt.displacementFee) || 0,
            reason: apt.reason,
            notes: apt.notes,
            cancellationReason: apt.cancellationReason,
            cancelledBy: apt.cancelledBy,
            cancelledAt: apt.cancelledAt,
            medicalRecordId: apt.medicalRecordId,
            videoRoomId: apt.videoRoomId,
            prescriptions: apt.prescriptions || [],
            certificates: apt.certificates || [],
            exams: apt.exams || [],
          };
        }),
        total: response.meta.total,
        page: response.meta.page,
        pageSize: response.meta.perPage,
      };
    },
  });

  const appointments = data?.appointments || [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'WAITING_PAYMENT':
      case 'PAYMENT_PENDING':
        return { bg: 'bg-yellow-100 dark:bg-yellow-950/40', text: 'text-yellow-800 dark:text-yellow-300', label: 'Pendente Pagamento' };
      case 'PAYMENT_FAILED':
        return { bg: 'bg-rose-100 dark:bg-rose-950/40', text: 'text-rose-700 dark:text-rose-300', label: 'Falha no Pagamento' };
      case 'PAID':
        return { bg: 'bg-blue-100 dark:bg-blue-950/40', text: 'text-blue-700 dark:text-blue-300', label: 'Pago' };
      case 'WAITING_DOCTOR':
        return { bg: 'bg-indigo-100 dark:bg-indigo-950/40', text: 'text-indigo-700 dark:text-indigo-300', label: 'Aguardando Médico' };
      case 'DOCTOR_ACCEPTED':
        return { bg: 'bg-teal-100 dark:bg-teal-950/40', text: 'text-teal-700 dark:text-teal-300', label: 'Médico Aceitou' };
      case 'IN_PROGRESS':
        return { bg: 'bg-emerald-100 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300', label: 'Em Andamento' };
      case 'FINISHED':
      case 'COMPLETED':
        return { bg: 'bg-gray-100 dark:bg-neutral-800', text: 'text-gray-700 dark:text-neutral-300', label: 'Concluída' };
      case 'CANCELLED':
        return { bg: 'bg-red-100 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-300', label: 'Cancelada' };
      case 'REFUNDED':
        return { bg: 'bg-purple-100 dark:bg-purple-950/40', text: 'text-purple-700 dark:text-purple-300', label: 'Estornada' };
      case 'PARTIALLY_REFUNDED':
        return { bg: 'bg-violet-100 dark:bg-violet-950/40', text: 'text-violet-700 dark:text-violet-300', label: 'Estorno Parcial' };
      case 'NO_SHOW':
        return { bg: 'bg-orange-100 dark:bg-orange-950/40', text: 'text-orange-700 dark:text-orange-300', label: 'Não Compareceu' };
      case 'SCHEDULED':
        return { bg: 'bg-blue-100 dark:bg-blue-950/40', text: 'text-blue-700 dark:text-blue-300', label: 'Agendada' };
      case 'RESCHEDULED':
        return { bg: 'bg-cyan-100 dark:bg-cyan-950/40', text: 'text-cyan-700 dark:text-cyan-300', label: 'Reagendada' };
      case 'ARCHIVED':
        return { bg: 'bg-slate-100 dark:bg-neutral-800', text: 'text-slate-700 dark:text-neutral-300', label: 'Arquivada' };
      case 'EXPIRED':
        return { bg: 'bg-zinc-100 dark:bg-zinc-800', text: 'text-zinc-700 dark:text-zinc-300', label: 'Expirada' };
      case 'CREATED':
        return { bg: 'bg-neutral-100 dark:bg-neutral-800', text: 'text-neutral-700 dark:text-neutral-300', label: 'Criada' };
      default:
        return { bg: 'bg-gray-100 dark:bg-neutral-800', text: 'text-gray-700 dark:text-neutral-300', label: status };
    }
  };

  const getServiceTypeBadge = (serviceType?: string) => {
    if (serviceType === 'DOMICILIAR') {
      return { bg: 'bg-amber-100 dark:bg-amber-950/40', text: 'text-amber-800 dark:text-amber-300', label: 'Domiciliar' };
    }
    if (serviceType === 'PRESENCIAL') {
      return { bg: 'bg-emerald-100 dark:bg-emerald-950/40', text: 'text-emerald-800 dark:text-emerald-300', label: 'Presencial' };
    }
    return { bg: 'bg-sky-100 dark:bg-sky-950/40', text: 'text-sky-800 dark:text-sky-300', label: 'Telemedicina' };
  };

  const handleStartVideo = (apt: Appointment) => {
    navigate(`/telemedicine/video/${apt.id}`);
  };

  const handleOpenCancelModal = (apt: Appointment) => {
    setAppointmentToCancel(apt);
    setCancelReason('');
    setCancelError(null);
  };

  const handleConfirmCancel = async () => {
    if (!appointmentToCancel) return;
    setIsCancelling(true);
    setCancelError(null);
    try {
      await adminApi.cancelAppointment(
        appointmentToCancel.id,
        cancelReason.trim() || undefined
      );
      setAppointmentToCancel(null);
      refetch();
    } catch (err: any) {
      setCancelError(err?.message || 'Falha ao cancelar consulta. Tente novamente.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleOpenRefundModal = (apt: Appointment) => {
    setAppointmentToRefund(apt);
    setRefundReason('');
    setRefundAmount(apt.price.toString());
    setRefundError(null);
    setRefundSuccess(null);
  };

  const handleConfirmRefund = async () => {
    if (!appointmentToRefund) return;
    setIsRefunding(true);
    setRefundError(null);
    setRefundSuccess(null);

    const parsedAmount = parseFloat(refundAmount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setRefundError('Valor de reembolso inválido.');
      setIsRefunding(false);
      return;
    }

    try {
      await adminApi.refundAppointment(
        appointmentToRefund.id,
        refundReason || undefined,
        parsedAmount
      );
      setRefundSuccess('Reembolso solicitado com sucesso.');
      refetch();
      setTimeout(() => {
        setAppointmentToRefund(null);
        setRefundSuccess(null);
      }, 1200);
    } catch (err: any) {
      setRefundError(err?.message || 'Falha ao solicitar reembolso. Tente novamente.');
    } finally {
      setIsRefunding(false);
    }
  };

  // Cálculo de estatísticas
  const totalCount = data?.total || appointments.length;
  const completedCount = appointments.filter(a => a.status === 'FINISHED' || a.status === 'COMPLETED').length;
  const activeCount = appointments.filter(a => ['IN_PROGRESS', 'DOCTOR_ACCEPTED', 'WAITING_DOCTOR', 'PAID'].includes(a.status)).length;
  const totalRevenue = appointments.reduce((sum, a) => sum + (a.status !== 'CANCELLED' && a.status !== 'REFUNDED' ? a.price : 0), 0);

  const getInitials = (name?: string) => {
    if (!name) return 'PT';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0]?.[0] || ''}${parts[1]?.[0] || ''}`.toUpperCase();
    }
    return (name.slice(0, 2) || 'PT').toUpperCase();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Gerenciamento de Consultas</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Acompanhamento clínico, financeiro e operacional de atendimentos
          </p>
        </div>
      </div>

      {/* Cards de Métricas Gerais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total de Consultas */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/60 flex items-center justify-center shrink-0">
            <Calendar className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Consultas</p>
            <p className="text-2xl font-black text-foreground mt-0.5 tracking-tight">{totalCount}</p>
          </div>
        </div>

        {/* Consultas Concluídas */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/60 flex items-center justify-center shrink-0">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Concluídas</p>
            <p className="text-2xl font-black text-foreground mt-0.5 tracking-tight">{completedCount}</p>
          </div>
        </div>

        {/* Em Andamento / Ativas */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 border border-purple-100 dark:border-purple-900/60 flex items-center justify-center shrink-0">
            <Activity className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Ativas / Fila</p>
            <p className="text-2xl font-black text-foreground mt-0.5 tracking-tight">{activeCount}</p>
          </div>
        </div>

        {/* Receita da Amostra */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-slate-200/90 dark:border-neutral-800 p-5 shadow-xs flex items-center gap-4 transition-all hover:shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-900/60 flex items-center justify-center shrink-0">
            <DollarSign className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Faturamento</p>
            <p className="text-2xl font-black text-foreground mt-0.5 tracking-tight">{formatCurrency(totalRevenue)}</p>
          </div>
        </div>
      </div>

      {/* Barra de Filtros Integrada */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por paciente, médico ou ID..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-xs transition-all"
          />
        </div>

        <select
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value as AppointmentStatusFilter);
            setPage(1);
          }}
          className="px-4 py-2.5 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-xs transition-all cursor-pointer"
        >
          <option value="ALL">Todos Status</option>
          <option value="WAITING_PAYMENT">Pendente Pagamento</option>
          <option value="PAID">Pago</option>
          <option value="WAITING_DOCTOR">Aguardando Médico</option>
          <option value="DOCTOR_ACCEPTED">Médico Confirmado</option>
          <option value="IN_PROGRESS">Em Andamento</option>
          <option value="FINISHED">Concluída</option>
          <option value="CANCELLED">Cancelada</option>
          <option value="NO_SHOW">Não Compareceu</option>
          <option value="REFUNDED">Reembolsado</option>
          <option value="RESCHEDULED">Reagendada</option>
          <option value="EXPIRED">Expirada</option>
        </select>

        <select
          value={serviceFilter}
          onChange={(e) => {
            setServiceFilter(e.target.value as ServiceTypeFilter);
            setPage(1);
          }}
          className="px-4 py-2.5 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-xs transition-all cursor-pointer"
        >
          <option value="ALL">Todas Modalidades</option>
          <option value="TELEMEDICINA">Telemedicina</option>
          <option value="PRESENCIAL">Presencial</option>
          <option value="DOMICILIAR">Domiciliar</option>
        </select>

        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
          className="px-4 py-2.5 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-xs transition-all cursor-pointer"
        >
          <option value="ALL">Todos Tipos</option>
          <option value="EXPRESS">Expressa</option>
          <option value="SCHEDULED">Agendada</option>
        </select>
      </div>

      {isLoading ? (
        <LoadingOverlay message="Carregando consultas..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar consultas" onRetry={() => refetch()} />
      ) : appointments.length === 0 ? (
        <EmptyState
          icon="Calendar"
          title="Nenhuma consulta encontrada"
          description={
            search || filter !== 'ALL' || typeFilter !== 'ALL' || serviceFilter !== 'ALL'
              ? 'Tente ajustar os filtros de busca'
              : 'Nenhuma consulta registrada ainda'
          }
          action={{ label: 'Atualizar', onClick: () => refetch() }}
        />
      ) : (
        <div className="rounded-2xl border border-slate-200/90 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/80 dark:bg-slate-800/60 border-b border-slate-200/80 dark:border-neutral-800">
                <tr>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Paciente</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Médico</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Especialidade</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Data/Hora</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Modalidade</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Tipo</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Valor</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-neutral-800/70 text-sm">
                {appointments.map((apt) => {
                  const statusBadge = getStatusBadge(apt.status);
                  const serviceBadge = getServiceTypeBadge(apt.serviceType);
                  const canStartVideo =
                    apt.serviceType !== 'DOMICILIAR' &&
                    ['WAITING_DOCTOR', 'DOCTOR_ACCEPTED', 'IN_PROGRESS', 'SCHEDULED', 'PAID'].includes(apt.status);
                  const canCancel =
                    apt.status !== 'CANCELLED' &&
                    apt.status !== 'FINISHED' &&
                    apt.status !== 'COMPLETED' &&
                    apt.status !== 'REFUNDED';
                  const canRefund =
                    (apt.status === 'PAID' || apt.status === 'FINISHED' || apt.status === 'CANCELLED') &&
                    apt.price > 0;

                  return (
                    <tr key={apt.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors duration-150">
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 via-primary to-indigo-600 text-white font-black text-xs flex items-center justify-center shadow-xs shrink-0 tracking-wider">
                            {getInitials(apt.patientName)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-foreground text-sm truncate">{apt.patientName}</p>
                            <p className="text-xs text-muted-foreground truncate">{apt.patientEmail}</p>
                            {apt.dependentName && (
                              <span className="inline-block mt-0.5 text-[10px] font-semibold text-purple-700 bg-purple-50 dark:bg-purple-950/40 px-1.5 py-0.2 rounded border border-purple-200">
                                Dep: {apt.dependentName} ({apt.dependentRelationship || 'familiar'})
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3.5">
                        <p className="font-bold text-foreground text-sm">{apt.doctorName}</p>
                        {apt.doctorCrm && (
                          <p className="text-[10px] font-mono text-muted-foreground">CRM {apt.doctorCrm}</p>
                        )}
                      </td>

                      <td className="px-4 py-3.5 text-slate-600 dark:text-slate-300 font-medium">
                        {apt.specialty}
                      </td>

                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 font-medium">
                          <Calendar className="h-3.5 w-3.5 text-slate-400" />
                          <span>{apt.date || '—'}</span>
                          <Clock className="h-3.5 w-3.5 text-slate-400 ml-1.5" />
                          <span>{apt.time || '—'}</span>
                        </div>
                      </td>

                      <td className="px-4 py-3.5">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${serviceBadge.bg} ${serviceBadge.text}`}
                        >
                          {serviceBadge.label}
                        </span>
                      </td>

                      <td className="px-4 py-3.5">
                        <span
                          className={`px-2.5 py-1 text-xs font-semibold rounded-full flex w-fit ${
                            apt.type === 'EXPRESS' ? 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200' : 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200'
                          }`}
                        >
                          {apt.type === 'EXPRESS' ? 'Expressa' : 'Agendada'}
                        </span>
                      </td>

                      <td className="px-4 py-3.5 font-bold text-foreground">
                        {formatCurrency(apt.price)}
                      </td>

                      <td className="px-4 py-3.5">
                        <span
                          className={`px-2.5 py-1 text-xs font-semibold rounded-full inline-block border ${statusBadge.bg} ${statusBadge.text}`}
                        >
                          {statusBadge.label}
                        </span>
                      </td>

                      <td className="px-4 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setSelectedAppointment(apt)}
                            className="p-1.5 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/60 hover:bg-blue-100 dark:hover:bg-blue-900/60 border border-blue-200/60 dark:border-blue-800 rounded-xl transition-all hover:scale-105 active:scale-95 cursor-pointer shadow-2xs"
                            title="Visualizar Detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </button>

                          {canStartVideo && (
                            <button
                              onClick={() => handleStartVideo(apt)}
                              className="p-1.5 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 border border-emerald-200/60 dark:border-emerald-800 rounded-xl transition-all hover:scale-105 active:scale-95 cursor-pointer shadow-2xs"
                              title="Iniciar Vídeo"
                            >
                              <Video className="h-4 w-4" />
                            </button>
                          )}

                          {canCancel && (
                            <button
                              onClick={() => handleOpenCancelModal(apt)}
                              className="p-1.5 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/60 hover:bg-red-100 dark:hover:bg-red-900/60 border border-red-200/60 dark:border-red-800 rounded-xl transition-all hover:scale-105 active:scale-95 cursor-pointer shadow-2xs"
                              title="Cancelar Consulta"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}

                          {canRefund && (
                            <button
                              onClick={() => handleOpenRefundModal(apt)}
                              className="p-1.5 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/60 hover:bg-amber-100 dark:hover:bg-amber-900/60 border border-amber-200/60 dark:border-amber-800 rounded-xl transition-all hover:scale-105 active:scale-95 cursor-pointer shadow-2xs"
                              title="Solicitar Reembolso"
                            >
                              <Undo2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200/80 dark:border-neutral-800 bg-slate-50/50 dark:bg-slate-900/30">
            <p className="text-xs font-medium text-muted-foreground">
              Mostrando {data?.total ? (page - 1) * 10 + 1 : 0} a {Math.min(page * 10, data?.total || 0)} de{' '}
              {data?.total || 0} consultas
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-2 border border-slate-200 dark:border-neutral-700 rounded-xl hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-2xs cursor-pointer"
                title="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-3.5 py-1 bg-primary text-white text-xs font-bold rounded-xl shadow-xs">{page}</span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page * 10 >= (data?.total || 0)}
                className="p-2 border border-slate-200 dark:border-neutral-700 rounded-xl hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-2xs cursor-pointer"
                title="Próxima página"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE DETALHES COMPLETOS DA CONSULTA (UX PREMIUM CLÍNICO) */}
      <Dialog open={!!selectedAppointment} onOpenChange={() => setSelectedAppointment(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0 rounded-3xl border border-slate-200 dark:border-neutral-800 shadow-2xl bg-card">
          <div className="p-5 bg-gradient-to-r from-blue-600 via-primary to-indigo-600 text-white flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <ShieldCheck className="w-5 h-5 text-blue-200" />
              <div>
                <DialogTitle className="text-base font-bold text-white">Ficha da Consulta</DialogTitle>
                <p className="text-xs text-blue-100 font-mono mt-0.5">#{selectedAppointment?.id}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSelectedAppointment(null)}
              className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors cursor-pointer"
              title="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {selectedAppointment && (
            <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(92vh-75px)] text-xs">
              {/* Header do Perfil da Consulta */}
              <div className="flex items-center justify-between pb-3.5 border-b border-slate-100 dark:border-neutral-800">
                <div>
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">Status da Consulta</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full inline-block border ${getStatusBadge(selectedAppointment.status).bg} ${getStatusBadge(selectedAppointment.status).text}`}>
                      {getStatusBadge(selectedAppointment.status).label}
                    </span>
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getServiceTypeBadge(selectedAppointment.serviceType).bg} ${getServiceTypeBadge(selectedAppointment.serviceType).text}`}>
                      {getServiceTypeBadge(selectedAppointment.serviceType).label}
                    </span>
                    <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-700 dark:bg-neutral-800 dark:text-neutral-300">
                      {selectedAppointment.type === 'EXPRESS' ? 'Expressa' : 'Agendada'}
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">Valor Total</span>
                  <p className="text-xl font-extrabold text-foreground mt-0.5">{formatCurrency(selectedAppointment.price)}</p>
                </div>
              </div>

              {/* Informações dos Envolvidos */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Card Paciente */}
                <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground flex items-center gap-1">
                    <User className="w-3 h-3 text-blue-600" />
                    <span>Dados do Paciente</span>
                  </span>
                  <p className="font-bold text-foreground text-sm">{selectedAppointment.patientName}</p>
                  <p className="text-muted-foreground flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    <span>{selectedAppointment.patientEmail}</span>
                  </p>
                  {selectedAppointment.patientPhone && (
                    <p className="text-muted-foreground flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      <span>{selectedAppointment.patientPhone}</span>
                    </p>
                  )}
                  {selectedAppointment.dependentName && (
                    <p className="text-purple-700 dark:text-purple-300 font-semibold pt-1">
                      Dependente atendido: {selectedAppointment.dependentName} ({selectedAppointment.dependentRelationship || 'familiar'})
                    </p>
                  )}
                </div>

                {/* Card Médico */}
                <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60 space-y-1.5">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-emerald-600" />
                    <span>Médico Responsável</span>
                  </span>
                  <p className="font-bold text-foreground text-sm">{selectedAppointment.doctorName}</p>
                  <p className="text-muted-foreground">{selectedAppointment.specialty}</p>
                  {selectedAppointment.doctorCrm && (
                    <p className="text-muted-foreground font-mono">CRM: {selectedAppointment.doctorCrm}</p>
                  )}
                  {selectedAppointment.doctorEmail && (
                    <p className="text-muted-foreground">{selectedAppointment.doctorEmail}</p>
                  )}
                </div>
              </div>

              {/* Data, Hora e Deslocamento */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground block mb-0.5">Data & Horário</span>
                  <span className="font-bold text-foreground text-xs">{selectedAppointment.date} às {selectedAppointment.time}</span>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground block mb-0.5">Modalidade</span>
                  <span className="font-bold text-foreground text-xs">{getServiceTypeBadge(selectedAppointment.serviceType).label}</span>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-neutral-800/50 border border-slate-200/60 dark:border-neutral-700/60">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground block mb-0.5">Deslocamento</span>
                  <span className="font-bold text-foreground text-xs">{formatCurrency(selectedAppointment.displacementFee || 0)}</span>
                </div>
              </div>

              {/* Queixa Principal / Motivo Clínico */}
              {(selectedAppointment.reason || selectedAppointment.notes) && (
                <div className="p-3.5 rounded-2xl bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200/60 dark:border-blue-900/40 space-y-1">
                  <span className="text-[10px] uppercase font-bold text-blue-700 dark:text-blue-300 block">Queixa / Motivo Clínico Relatado</span>
                  <p className="text-foreground leading-relaxed">{selectedAppointment.reason || selectedAppointment.notes}</p>
                </div>
              )}

              {/* Resumo de Documentos Digitais Emitidos (Receitas, Atestados, Exames) */}
              <div className="space-y-2">
                <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">
                  Documentos Digitais & Prescrições (MeMed ICP-Brasil)
                </span>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {/* Receitas */}
                  <div className="p-3 rounded-xl bg-emerald-50/80 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                    <div className="flex items-center gap-1.5 font-bold text-emerald-800 dark:text-emerald-300 mb-1">
                      <Pill className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Receitas ({selectedAppointment.prescriptions?.length || 0})</span>
                    </div>
                    {selectedAppointment.prescriptions && selectedAppointment.prescriptions.length > 0 ? (
                      <div className="space-y-1 pt-1">
                        {selectedAppointment.prescriptions.map((p) => (
                          <div key={p.id} className="flex items-center justify-between text-[11px]">
                            <span className="truncate">{p.memedStatus || 'Assinada'}</span>
                            {p.memedPdfUrl && (
                              <a href={p.memedPdfUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-bold hover:underline inline-flex items-center gap-0.5">
                                <ExternalLink className="w-3 h-3" /> PDF
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">Nenhuma receita emitida</p>
                    )}
                  </div>

                  {/* Atestados */}
                  <div className="p-3 rounded-xl bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-1.5 font-bold text-amber-800 dark:text-amber-300 mb-1">
                      <Award className="w-3.5 h-3.5 text-amber-600" />
                      <span>Atestados ({selectedAppointment.certificates?.length || 0})</span>
                    </div>
                    {selectedAppointment.certificates && selectedAppointment.certificates.length > 0 ? (
                      <div className="space-y-1 pt-1">
                        {selectedAppointment.certificates.map((c) => (
                          <div key={c.id} className="flex items-center justify-between text-[11px]">
                            <span className="truncate">{c.diagnosis || 'Repouso médico'}</span>
                            {c.pdfUrl && (
                              <a href={c.pdfUrl} target="_blank" rel="noopener noreferrer" className="text-amber-700 font-bold hover:underline inline-flex items-center gap-0.5">
                                <ExternalLink className="w-3 h-3" /> PDF
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">Nenhum atestado emitido</p>
                    )}
                  </div>

                  {/* Exames */}
                  <div className="p-3 rounded-xl bg-purple-50/80 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-1.5 font-bold text-purple-800 dark:text-purple-300 mb-1">
                      <FileText className="w-3.5 h-3.5 text-purple-600" />
                      <span>Exames ({selectedAppointment.exams?.length || 0})</span>
                    </div>
                    {selectedAppointment.exams && selectedAppointment.exams.length > 0 ? (
                      <div className="space-y-1 pt-1">
                        {selectedAppointment.exams.map((e) => (
                          <div key={e.id} className="flex items-center justify-between text-[11px]">
                            <span className="truncate">{e.type || 'Complementar'}</span>
                            <span className="text-[10px] uppercase font-bold text-purple-700">{e.status || 'Solicitado'}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">Nenhum exame solicitado</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Se Cancelada ou Estornada */}
              {(selectedAppointment.status === 'CANCELLED' || selectedAppointment.cancellationReason) && (
                <div className="p-3.5 rounded-2xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 text-red-800 dark:text-red-300 space-y-1">
                  <span className="text-[10px] uppercase font-bold block">Detalhes do Cancelamento</span>
                  <p>Motivo: {selectedAppointment.cancellationReason || 'Não especificado'}</p>
                  {selectedAppointment.cancelledBy && (
                    <p className="text-[11px] text-red-600 dark:text-red-400">Cancelado por: {selectedAppointment.cancelledBy}</p>
                  )}
                </div>
              )}

              {/* Botões de Ação no Rodapé do Modal */}
              <div className="pt-2 flex items-center justify-between gap-3 border-t border-slate-100 dark:border-neutral-800">
                {selectedAppointment.videoRoomId && (
                  <Button
                    size="sm"
                    onClick={() => handleStartVideo(selectedAppointment)}
                    className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Video className="w-3.5 h-3.5" />
                    <span>Entrar na Sala Virtual</span>
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedAppointment(null)}
                  className="rounded-xl px-5 text-xs font-semibold ml-auto"
                >
                  Fechar Ficha
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Cancelamento Administrativo */}
      <Dialog open={!!appointmentToCancel} onOpenChange={() => !isCancelling && setAppointmentToCancel(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              <DialogTitle>Cancelar Consulta</DialogTitle>
            </div>
          </DialogHeader>

          {appointmentToCancel && (
            <div className="p-6 pt-2 space-y-4 text-xs">
              <p className="text-sm text-gray-600 dark:text-neutral-300">
                Tem certeza de que deseja cancelar a consulta do paciente{' '}
                <strong className="text-gray-900 dark:text-white">{appointmentToCancel.patientName}</strong> com o médico{' '}
                <strong className="text-gray-900 dark:text-white">{appointmentToCancel.doctorName}</strong>?
              </p>

              <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/40 rounded-lg text-xs text-amber-800 dark:text-amber-300 space-y-1">
                <p className="font-semibold">Política de cancelamento administrativo:</p>
                <p>
                  Caso a consulta já possua pagamento confirmado, o reembolso integral no valor de{' '}
                  <strong>{formatCurrency(appointmentToCancel.price)}</strong> será solicitado automaticamente.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                  Motivo do cancelamento (opcional)
                </label>
                <textarea
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Ex.: Solicitação do paciente, indisponibilidade técnica, etc."
                  disabled={isCancelling}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100 dark:bg-neutral-800"
                />
              </div>

              {cancelError && (
                <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 rounded-lg text-xs text-red-700 dark:text-red-300">
                  {cancelError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setAppointmentToCancel(null)}
                  disabled={isCancelling}
                  className="text-xs"
                >
                  Voltar
                </Button>
                <Button
                  onClick={handleConfirmCancel}
                  disabled={isCancelling}
                  className="bg-red-600 hover:bg-red-700 text-white text-xs"
                >
                  {isCancelling ? 'Cancelando...' : 'Confirmar Cancelamento'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Reembolso Administrativo */}
      <Dialog open={!!appointmentToRefund} onOpenChange={() => !isRefunding && setAppointmentToRefund(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2 text-amber-600">
              <Undo2 className="h-5 w-5" />
              <DialogTitle>Solicitar Reembolso</DialogTitle>
            </div>
          </DialogHeader>

          {appointmentToRefund && (
            <div className="p-6 pt-2 space-y-4 text-xs">
              <p className="text-sm text-gray-600 dark:text-neutral-300">
                Você está solicitando o estorno da consulta de{' '}
                <strong className="text-gray-900 dark:text-white">{appointmentToRefund.patientName}</strong> no valor de{' '}
                <strong className="text-gray-900 dark:text-white">{formatCurrency(appointmentToRefund.price)}</strong>.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                  Valor do Reembolso (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={appointmentToRefund.price}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  disabled={isRefunding}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:bg-gray-100 dark:bg-neutral-800"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                  Motivo do Reembolso (opcional)
                </label>
                <textarea
                  rows={2}
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Ex.: Acordo comercial, insatisfação justificada, etc."
                  disabled={isRefunding}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:bg-gray-100 dark:bg-neutral-800"
                />
              </div>

              {refundError && (
                <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 rounded-lg text-xs text-red-700 dark:text-red-300">
                  {refundError}
                </div>
              )}

              {refundSuccess && (
                <div className="p-3 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900/40 rounded-lg text-xs text-green-700 dark:text-green-300">
                  {refundSuccess}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setAppointmentToRefund(null)}
                  disabled={isRefunding}
                  className="text-xs"
                >
                  Voltar
                </Button>
                <Button
                  onClick={handleConfirmRefund}
                  disabled={isRefunding}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                >
                  {isRefunding ? 'Processando...' : 'Confirmar Reembolso'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
